import { DataValue } from '@apollo/client/react/hoc';
import React from 'react';
import I18n from 'i18n-js';

import { conditionSlugForUuid } from '@reverbdotcom/commons/src/condition_helpers';
import { I18N } from '@reverbdotcom/commons/src/components/translate';
import { connect } from '@reverbdotcom/commons/src/connect';
import { useUser } from '@reverbdotcom/commons/src/user_hooks';
import { useViewTracking } from '@reverbdotcom/commons/src/use_tracking';
import { MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_types';
import experiments from '@reverbdotcom/commons/src/experiments';
import { formatExperimentsForEventAttributes } from '@reverbdotcom/commons/src/elog/mparticle';
import { trackEvent } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import {
  CSP,
  DataServicesPriceGuideCpEstimates,
  DataServicesPriceGuideCspEstimates,
  SearchPriceGuideTotalOrders,
} from '@reverbdotcom/commons/src/rql';

import PriceEstimates from '../shared/price_estimates';
import V2CSPPriceGuideModuleOrdersEmptyState from './v2_csp_price_guide_module_orders_empty_state';

import {
  withCSPPriceRecQuery,
  withCPriceRecQuery,
  withTotalOrdersQuery,
} from './queries';

import { determineEstimateFromMultipleConditions } from '../shared/helper';
import V2CSPPriceGuideModuleOrdersContainer from './v2_csp_price_guide_module_orders_container';

export const ORDER_THRESHOLD_FOR_ESTIMATE_SUBTITLE = 10;
export const THRESHOLD_FOR_ORDERS_SECTION = 1;
interface IExternalProps {
  csp: CSP;
  conditionUuids: string[];
  canonicalProductId?: string;
}

interface ICustomDataProps {
  cpEstimateData: DataValue<DataServicesPriceGuideCpEstimates.Query, DataServicesPriceGuideCpEstimates.Variables >;
  cspEstimateData: DataValue<DataServicesPriceGuideCspEstimates.Query, DataServicesPriceGuideCspEstimates.Variables >;
  ordersData: DataValue<SearchPriceGuideTotalOrders.Query, SearchPriceGuideTotalOrders.Variables>;
}

type IProps = IExternalProps & ICustomDataProps;

function estimateToRender({
  canonicalProductId,
  cpEstimate,
  cspEstimate,
}) {
  return canonicalProductId ? cpEstimate : cspEstimate;
}

function determineEstimate(estimates) {
  const sanitizedEstimates = estimates.filter(estimate => !!estimate?.priceMiddle);
  const hasMultiplesEstimates = sanitizedEstimates.length > 1;
  const hasNoEstimates = sanitizedEstimates.length < 1;

  if (hasNoEstimates) return {};

  if (hasMultiplesEstimates) {
    return determineEstimateFromMultipleConditions(sanitizedEstimates);
  } else {
    return estimates[0] || {};
  }
}

function estimateSubtitle({ totalOrders, conditionUuids }) {
  const hasOneCondition = conditionUuids.length === 1;
  const baseI18nString = 'discovery.priceGuideTool.cspModule.v2Layout.priceEstimates';
  const belowThresholdForSpecificOrderCount = totalOrders < ORDER_THRESHOLD_FOR_ESTIMATE_SUBTITLE;

  if (hasOneCondition) {
    return belowThresholdForSpecificOrderCount ?
      `${baseI18nString}.subtitleWithOrderBelowThresholdAndOneCondition` :
      `${baseI18nString}.subtitleOneCondition`;
  } else {
    return belowThresholdForSpecificOrderCount ?
      `${baseI18nString}.subtitleWithOrderBelowThresholdAndAllConditions` :
      `${baseI18nString}.subtitleAllConditions`;
  }
}

interface TrackFilteredEstimateArgs {
  shouldTrack: boolean;
  csp: CSP;
  hasEstimate: boolean;
  canonicalProductId?: string;
  selectedConditionSlug?: string;
}

function trackFilteredEstimate({
  shouldTrack,
  csp,
  canonicalProductId,
  selectedConditionSlug,
  hasEstimate,
}: TrackFilteredEstimateArgs) {
  if (shouldTrack) {
    trackEvent({
      eventName: MParticleEventName.ViewedFilteredCSPPriceGuideModule,
      componentName: 'V2CSPPriceGuideModuleContent',
      cspId: csp.id,
      cpId: canonicalProductId,
      conditionSlug: selectedConditionSlug,
      hasDataServicesPriceEstimate: hasEstimate,
    });
  }
}

export function V2CSPPriceGuideModuleContent({
  csp,
  conditionUuids,
  cpEstimateData,
  cspEstimateData,
  ordersData,
  canonicalProductId,
}: IProps) {
  const user = useUser();

  const {
    loading: cpEstimateLoading,
    priceRecommendations = {},
  } = cpEstimateData ?? {};

  const {
    loading: cspEstimateLoading,
    cspPriceRecommendations = {},
  } = cspEstimateData ?? {};

  const {
    loading: ordersLoading,
    priceRecordsSearch = {},
  } = ordersData ?? {};

  const { total: totalOrders = 0 } = priceRecordsSearch;

  const cspEstimates = cspPriceRecommendations.priceRecommendations || [];
  const cspEstimate = determineEstimate(cspEstimates);

  const cpEstimates = priceRecommendations.priceRecommendations || [];
  const cpEstimate = determineEstimate(cpEstimates);

  const estimate = estimateToRender({
    canonicalProductId,
    cpEstimate,
    cspEstimate,
  });

  const hasEstimate = !!estimate?.priceMiddle;

  const hasInvalidPriceLow = Number(estimate?.priceLow?.amountCents) <= 0;

  const hasModelSelected = !!canonicalProductId;
  const model = hasModelSelected ?
    csp.variants.find(variant => variant.id == canonicalProductId) :
    { name: I18n.t('discovery.priceGuideTool.cspModule.v2Layout.priceEstimates.allModels') };

  const canonicalProductIds = hasModelSelected ? [canonicalProductId] : csp.variants.map(variant => variant.id);

  const hasConditionSelected = conditionUuids.length == 1;
  const selectedConditionSlug = hasConditionSelected ? conditionSlugForUuid(conditionUuids[0]) : null;

  const isDoneLoadingCspEstimate = !cspEstimateLoading;
  const isDoneLoadingCpEstimate = !cpEstimateLoading;
  const isDoneLoadingAllEstimates = isDoneLoadingCspEstimate && isDoneLoadingCpEstimate;
  const isDoneLoadingOrderData = !ordersLoading;

  const hasFilterApplied = hasConditionSelected || hasModelSelected;
  const shouldTrack = isDoneLoadingAllEstimates && hasFilterApplied;

  trackFilteredEstimate({
    shouldTrack,
    csp,
    canonicalProductId,
    selectedConditionSlug,
    hasEstimate,
  });

  const logDefaultCspEstimate = isDoneLoadingCspEstimate && !hasConditionSelected;

  useViewTracking({
    eventName: MParticleEventName.CSPPriceGuideModuleRendered,
    componentName: 'V2CSPPriceGuideModuleLayout',
    cspId: csp.id,
    cspSlug: csp.slug,
    hasDataServicesPriceEstimate: hasEstimate,
    experiments: formatExperimentsForEventAttributes(user, [
      experiments.PRICE_GUIDE_TOOL_CSP_MODULE_V2,
    ]),
  }, logDefaultCspEstimate);

  const shouldDisplayOrdersContainer = isDoneLoadingOrderData && totalOrders >= THRESHOLD_FOR_ORDERS_SECTION;
  const shouldDisplayOrdersEmptyState = isDoneLoadingOrderData && totalOrders < THRESHOLD_FOR_ORDERS_SECTION;

  return (
    <div className="v2-csp-price-guide-module-content">
      {hasEstimate && !hasInvalidPriceLow && (
        <div className="v2-csp-price-guide-module-content__estimates-container">
          <div className="v2-csp-price-guide-module-content__estimates-container__header">
            <div className="v2-csp-price-guide-module-content__estimates-container__header__title">
              {I18n.t('discovery.priceGuideTool.cspModule.v2Layout.priceEstimates.title')}
            </div>
            <I18N
              html
              text={estimateSubtitle({ totalOrders, conditionUuids })}
              args={{
                model: model.name,
                condition: I18n.t(`discovery.conditions.${selectedConditionSlug}`),
                total: totalOrders.toLocaleString(),
              }}
            />
          </div>

          <div className="v2-csp-price-guide-module-content__estimates-container__estimate">
            <PriceEstimates
              priceLow={estimate.priceLow}
              priceMiddle={estimate.priceMiddle}
              priceHigh={estimate.priceHigh}
              priceMiddleThirtyDaysAgo={estimate.priceMiddleThirtyDaysAgo}
              hideGreatValueEstimate={!hasModelSelected || !hasConditionSelected}
            />
          </div>
        </div>
      )}

      {shouldDisplayOrdersContainer && (
        <V2CSPPriceGuideModuleOrdersContainer
          csp={csp}
          canonicalProductIds={canonicalProductIds}
          conditionUuids={conditionUuids}
        />
      )}

      {shouldDisplayOrdersEmptyState && (
        <V2CSPPriceGuideModuleOrdersEmptyState />
      )}
    </div>
  );
}

export default connect<IExternalProps>([
  withCSPPriceRecQuery,
  withCPriceRecQuery,
  withTotalOrdersQuery,
])(V2CSPPriceGuideModuleContent);
